import { useEffect, useId, useState } from 'react';
import { Alert, Button, FormField, Modal } from '../../ui';
import PanPicker, { panErrorMessages } from '../pages/sub-merchants/PanPicker';

const formFrom = (subMerchant) => ({
  name: subMerchant?.name ?? '',
  email: subMerchant?.email ?? '',
  phone_number: subMerchant?.phone_number ?? '',
});

const createPayload = (form, pans) => {
  const payload = { name: form.name.trim() };
  if (form.email.trim()) payload.email = form.email.trim();
  if (form.phone_number.trim()) payload.phone_number = form.phone_number.trim();
  if (pans.length > 0) payload.pans = pans;
  return payload;
};

const updatePayload = (form, subMerchant) => {
  const payload = {};
  if (form.name.trim() !== subMerchant.name) payload.name = form.name.trim();
  ['email', 'phone_number'].forEach((field) => {
    const next = form[field].trim() || null;
    if (next !== (subMerchant[field] ?? null)) payload[field] = next;
  });
  return payload;
};

export default function SubMerchantFormModal({ isOpen, subMerchant = null, onClose, onSubmit }) {
  const isEdit = Boolean(subMerchant);
  const [form, setForm] = useState(formFrom(subMerchant));
  const [pans, setPans] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const fieldId = useId();
  const formId = `${fieldId}-form`;

  useEffect(() => {
    setForm(formFrom(subMerchant));
    setPans([]);
    setFieldErrors({});
    setError('');
  }, [subMerchant, isOpen]);

  const setField = (name) => (e) => setForm({ ...form, [name]: e.target.value });
  const fieldError = (name) => fieldErrors[name]?.[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = isEdit ? updatePayload(form, subMerchant) : createPayload(form, pans);
    if (isEdit && Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    setError('');
    setFieldErrors({});
    setLoading(true);
    try {
      await onSubmit(payload);
      onClose();
    } catch (err) {
      const errors = err.response?.data?.errors ?? {};
      const panMessages = panErrorMessages(errors);
      setFieldErrors(errors);
      setError(
        panMessages.length > 0
          ? panMessages.join(' ')
          : err.response?.data?.message || 'Something went wrong. Please try again.'
      );
    }
    setLoading(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit sub-merchant' : 'New sub-merchant'}
      size={isEdit ? 'md' : 'lg'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={loading}>
            {isEdit ? 'Save Changes' : 'Create Sub-merchant'}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-4" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <form id={formId} onSubmit={handleSubmit} noValidate>
        <FormField label="Name" htmlFor={`${fieldId}-name`} error={fieldError('name')} required>
          <input
            id={`${fieldId}-name`}
            className="form-control"
            maxLength={255}
            value={form.name}
            onChange={setField('name')}
            required
          />
        </FormField>

        <div className="grid gap-x-4 sm:grid-cols-2">
          <FormField
            label="Contact Email"
            htmlFor={`${fieldId}-email`}
            error={fieldError('email')}
            hint="Optional. This is not a login; invite users separately."
          >
            <input
              id={`${fieldId}-email`}
              type="email"
              className="form-control"
              maxLength={255}
              value={form.email}
              onChange={setField('email')}
            />
          </FormField>

          <FormField label="Contact Phone" htmlFor={`${fieldId}-phone`} error={fieldError('phone_number')} hint="Optional.">
            <input
              id={`${fieldId}-phone`}
              className="form-control"
              maxLength={15}
              value={form.phone_number}
              onChange={setField('phone_number')}
            />
          </FormField>
        </div>
      </form>

      {!isEdit && (
        <fieldset className="mb-4">
          <legend className="form-label">PANs</legend>
          <PanPicker selected={pans} onChange={setPans} />
          <p className="mt-1 text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
            Optional. You can assign PANs later.
          </p>
        </fieldset>
      )}
    </Modal>
  );
}
